import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { readEmailLinkToken } from "@/lib/emailLinkParams";
import KinAvatar from "@/components/KinAvatar";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  /**
   * The link didn't work — expired, already used, or it carried nothing (#626).
   *
   * ⚠️ Separate from `ready` on purpose. Before this, `if (!ready)` rendered
   * "Loading…" with no timeout and no failure branch, so a dead reset link left
   * her staring at a spinner forever. That is the third wrong answer #744 named:
   * it looks like the app is trying, so she waits instead of asking for a new
   * link. Adding verifyOtp gives that state one MORE way to happen, so it could
   * not be left as it was.
   */
  const [linkFailed, setLinkFailed] = useState(false);
  // verifyOtp burns a single-use token. StrictMode double-invokes effects in
  // dev, and a second call on a spent token fails — which would report "expired"
  // on a link that had just worked.
  const verifyStarted = useRef(false);

  useEffect(() => {
    // Listen for PASSWORD_RECOVERY event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setReady(true);
      }
    });
    // Also check URL hash
    if (window.location.hash.includes("type=recovery")) {
      setReady(true);
    }
    return () => subscription.unsubscribe();
  }, []);

  // ── The same-domain link shape (#626) ──────────────────────────────────
  //
  // A reset email built against our own domain arrives as
  // /reset-password?token_hash=…&type=recovery. The tap target is then an
  // ASSOCIATED DOMAIN, so iOS can hand it to the Median app instead of Safari.
  // Supabase's default template points at supabase.co and 302s here, and iOS
  // never fires a Universal Link on a server-side redirect — which is why reset
  // has always opened in a browser. Same root cause as the signup link.
  //
  // ⚠️ BOTH shapes stay live. Reset emails already in inboxes carry the hash
  // form, and the template is a Dashboard setting flipped by hand — there is no
  // moment when they all switch over. The two effects are independent: this one
  // returns and does nothing when the link is the older kind.
  useEffect(() => {
    if (verifyStarted.current) return;
    const token = readEmailLinkToken(window.location.search);
    // Only `recovery` on this screen. A signup link that somehow landed here
    // should NOT be exchanged into a password-change session.
    if (!token || token.type !== "recovery") return;
    verifyStarted.current = true;

    void (async () => {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: token.tokenHash,
        type: "recovery",
      });
      if (error) {
        console.error("[ResetPassword] verifyOtp failed:", error.message);
        setLinkFailed(true);
        return;
      }
      // The session now exists in whatever opened the link — the app, if iOS
      // handed it over. updateUser() below acts on that session.
      setReady(true);
    })();
  }, []);

  // ⚠️ A link that produces NOTHING must not leave her on a spinner. Six
  // seconds matches AuthCallback's grace window for the hash form to parse.
  useEffect(() => {
    if (ready) return;
    const t = setTimeout(() => setLinkFailed(true), 6000);
    return () => clearTimeout(t);
  }, [ready]);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setError(error.message);
      setLoading(false);
      return;
    }
    navigate("/");
  };

  // ⚠️ The failure branch is checked BEFORE the loading branch. A dead link
  // sets linkFailed while ready stays false, so checking `!ready` first would
  // keep the spinner up over a link that has already been ruled out.
  if (linkFailed && !ready) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center px-6" style={{ backgroundColor: "#F0F0FF" }}>
        <div className="w-full max-w-sm flex flex-col items-center text-center gap-3">
          <KinAvatar size="lg" variant="dark" />
          <p className="font-body text-sm" style={{ color: "#54525C" }}>
            That reset link's expired — they only last an hour. Ask for a new one and I'll send it right over.
          </p>
          <button
            onClick={() => navigate("/signin")}
            className="font-body"
            style={{ fontSize: 14, fontWeight: 600, color: "#5050C8", background: "none", border: "none", padding: 0, cursor: "pointer" }}
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center" style={{ backgroundColor: "#F0F0FF" }}>
        <p className="font-body text-sm" style={{ color: "#54525C" }}>Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center px-6" style={{ backgroundColor: "#F0F0FF" }}>
      <div className="w-full max-w-sm flex flex-col items-center">
        <KinAvatar size="lg" variant="dark" />
        <h1 className="font-display text-center mt-5 mb-6" style={{ fontSize: "28px", color: "#1C1A3E" }}>
          Set your new password
        </h1>
        <form onSubmit={handleReset} className="w-full flex flex-col gap-4">
          <input
            type="password"
            placeholder="New password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full h-12 px-4 rounded-xl border font-body text-base bg-white focus:outline-none focus:ring-2 transition-all"
            style={{ borderColor: "#C8C8F4", color: "#1C1A3E" }}
          />
          <div>
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full h-12 px-4 rounded-xl border font-body text-base bg-white focus:outline-none focus:ring-2 transition-all"
              style={{ borderColor: "#C8C8F4", color: "#1C1A3E" }}
            />
            {error && <p className="text-sm font-body mt-1" style={{ color: "#C8C8F4" }}>{error}</p>}
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 font-body font-semibold text-base text-white transition-all hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: "#5050C8", borderRadius: "14px" }}
          >
            {loading ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
